/**
 * Depreciation (piece 28): an optional module over capitalised asset nodes.
 * Only straight-line and declining-balance methods are supported. 🔨 Register
 * membership is never gated by a capitalisation threshold: whether a schedule
 * exists answers whether the asset is on the balance sheet, and nothing else.
 * The arithmetic is in whole cents.
 */
import { roundHalfUpCents } from '../money';
export const DEPRECIATION_METHODS = ['StraightLine', 'DecliningBalance'] as const;
export type DepreciationMethod = (typeof DEPRECIATION_METHODS)[number];

export interface DepreciationPeriod {
  /** 1 for the first month of the schedule. */
  period: number;
  amountCents: number;
  bookValueCents: number;
}

/**
 * One row per month. Straight-line spreads cost less salvage evenly, the last
 * month taking the remainder. Declining-balance takes twice the straight-line
 * rate of the book value each month, never below salvage, and the last month
 * brings the book value to salvage.
 */
export function depreciationPeriods(costCents: number, salvageCents: number, usefulLifeMonths: number, method: DepreciationMethod): DepreciationPeriod[] {
  if (!Number.isInteger(usefulLifeMonths) || usefulLifeMonths <= 0 || costCents <= salvageCents) return [];
  const periods: DepreciationPeriod[] = [];
  let book = costCents;
  const depreciable = costCents - salvageCents;
  for (let period = 1; period <= usefulLifeMonths; period++) {
    const last = period === usefulLifeMonths;
    let amount: number;
    if (method === 'StraightLine') amount = last ? book - salvageCents : Math.floor(depreciable / usefulLifeMonths);
    else amount = last ? book - salvageCents : Math.min(book - salvageCents, roundHalfUpCents(book * (2 / usefulLifeMonths)));
    amount = Math.max(0, amount);
    book -= amount;
    periods.push({ period, amountCents: amount, bookValueCents: book });
  }
  return periods;
}

export function depreciationProblem(input: { usefulLifeMonths: number; salvageValueCents: number; acquisitionCostCents: number | null }): string | null {
  if (input.acquisitionCostCents === null) return 'a depreciation schedule needs the asset’s acquisition cost first';
  if (!Number.isInteger(input.usefulLifeMonths) || input.usefulLifeMonths <= 0) return 'a useful life is a whole number of months above zero';
  if (input.salvageValueCents < 0) return 'a salvage value is zero or more';
  if (input.salvageValueCents >= input.acquisitionCostCents) return 'the salvage value is below what the asset cost';
  return null;
}

// ---- One period's posting (decision 9141; T2367) ----

/** A depreciation period is a calendar month, written `YYYY-MM`. */
export const DEPRECIATION_MONTH_PATTERN = /^\d{4}-(0[1-9]|1[0-2])$/;

const monthIndex = (month: string): number => Number(month.slice(0, 4)) * 12 + Number(month.slice(5, 7)) - 1;
const monthOf = (index: number): string => `${Math.floor(index / 12)}-${String((index % 12) + 1).padStart(2, '0')}`;

/** The schedule's period a month is: 1 for the start date's own month, null before it or after the useful life. */
export function depreciationPeriodOf(startDate: string, month: string, usefulLifeMonths: number): number | null {
  const period = monthIndex(month) - monthIndex(startDate.slice(0, 7)) + 1;
  return period >= 1 && period <= usefulLifeMonths ? period : null;
}

/** The month's last day, `YYYY-MM-DD`, the day its entry is dated. */
export function depreciationMonthEnd(month: string): string {
  const next = monthOf(monthIndex(month) + 1);
  const last = new Date(Date.UTC(Number(next.slice(0, 4)), Number(next.slice(5, 7)) - 1, 0));
  return last.toISOString().slice(0, 10);
}

/**
 * 🔒 The entry's reference, which is also what makes a period post once: one
 * schedule's one month. A run that finds a standing entry under it posts
 * nothing; a reversed entry no longer stands, so the period can post again.
 */
export function depreciationReference(month: string, scheduleId: string): string {
  return `DEP-${month}-${scheduleId}`;
}

/** The schedule's months from its start through the month a day falls in, newest first, the ones a person can post. */
export function depreciationMonthsThrough(startDate: string, usefulLifeMonths: number, throughDay: string): string[] {
  const first = monthIndex(startDate.slice(0, 7));
  const last = Math.min(first + usefulLifeMonths - 1, monthIndex(throughDay.slice(0, 7)));
  const months: string[] = [];
  for (let index = last; index >= first; index--) months.push(monthOf(index));
  return months;
}

/** 🔒 Decision 9860: the To-Do Create Asset raises so somebody sets the new asset's depreciation; it closes when a schedule is written on the asset. */
export const ASSET_DEPRECIATION_KIND = 'AssetDepreciationToSet' as const;
/** The To-Do's source: the asset it stands for, so its row opens that asset. */
export const ASSET_DEPRECIATION_SOURCE = 'AssetNode' as const;
/** The queue the To-Do lands on (piece 12's registry), the Accounting group's, since a queue's gate is a capability and the register's module is not one. */
export const ASSET_DEPRECIATION_QUEUE_KEY = 'queue:asset-depreciation' as const;
/** The register's narrowing the queue opens on: `/assets?needs=depreciation` lists only the assets whose To-Do is still open, each opening its own page. */
export const ASSET_NEEDS_DEPRECIATION = 'depreciation' as const;
/** 🔒 The queue's route (piece 12's table): the asset register narrowed to the assets an open To-Do of the kind stands for. */
export const ASSET_DEPRECIATION_QUEUE_ROUTE = `/assets?needs=${ASSET_NEEDS_DEPRECIATION}` as const;

/** 🔒 Who runs a period's depreciation: the capability every posting route reads (decision 9141), the Accounting and Admin roles'. */
export const DEPRECIATION_POST_CAPABILITY = 'accounting.post';
